import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import StickyCTA from "@/components/StickyCTA";
import {
  COURSES, COURSE_LIST, COURSE_INFO, RESULTS, INCLUDED_DETAILED,
  TRUST_BOOKING, TRAINER, GOODIE_BAG_PHOTO, WHATSAPP_DM_URL,
  api, trackEvent,
} from "@/lib/api";
import { DEFAULT_CONTENT, mergeContent, buildWhatsAppUrl, formatUrgency } from "@/lib/siteContent";
import {
  ArrowUpRight, Check, Info, X, Clock, MessageCircle, Shield,
  ChevronDown, ChevronUp, Star, Sparkles, HelpCircle, Target, Users,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from "@/components/ui/dialog";

// The featured course = Weave met Leave-Out (17-19 juli).
const FEATURED = COURSES.weave_leaveout;
const OTHER_COURSES = COURSE_LIST.filter((c) => c.id !== FEATURED.id);

export default function CourseSelection({ previewContent = null }) {
  const navigate = useNavigate();
  const [availability, setAvailability] = useState({});
  const [publishedContent, setPublishedContent] = useState(null);

  // Merge: preview > published > defaults
  const content = useMemo(() => {
    const base = mergeContent(DEFAULT_CONTENT, publishedContent);
    if (previewContent) return mergeContent(base, previewContent);
    return base;
  }, [publishedContent, previewContent]);

  const waUrl = useMemo(() => buildWhatsAppUrl(content.whatsapp), [content.whatsapp]);
  const cmsCourseById = useMemo(() => {
    const map = {};
    for (const c of (content.courses || [])) map[c.id] = c;
    return map;
  }, [content.courses]);

  useEffect(() => {
    // In preview mode we don't fetch anything.
    if (previewContent) return;
    let cancelled = false;
    api.get("/availability")
      .then(({ data }) => { if (!cancelled) setAvailability(data); })
      .catch(() => {});
    api.get("/site-content/published")
      .then(({ data }) => { if (!cancelled && data && Object.keys(data).length) setPublishedContent(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [previewContent]);

  const featuredRemaining = availability[FEATURED.id]?.remaining ?? FEATURED.totalSpots;
  const goToReserve = () => navigate(`/register/${FEATURED.id}`);
  const heroTitleLine1 = content.hero.title_line1;
  const heroTitleItalic = content.hero.title_italic;
  const heroTitleLine2 = content.hero.title_line2;
  const subtitleText = (content.hero.subtitle || "").replace(/\{n\}/g, String(featuredRemaining));
  const urgencyText = formatUrgency(content.hero.urgency_template, featuredRemaining);
  const announcement = content.announcement || {};
  const faq = content.faq || [];

  return (
    <Layout step={1}>
      {/* Announcement banner (CMS-controlled) */}
      {announcement.enabled && (announcement.text || "").trim() && (
        <div className="mb-4 rounded-2xl bg-black text-[#FF6A1A] border-2 border-black px-4 py-3 text-sm font-semibold text-center" data-testid="announcement-banner">
          {announcement.text}
        </div>
      )}
      {/* ============ FOCUSED HERO — WEAVE MET LEAVE-OUT ============ */}
      <section className="pt-2 pb-8">
        <div className="label-tag mb-3">{content.hero.label}</div>

        {/* Urgency badge */}
        <div className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase mb-4 animate-pulse" data-testid="hero-urgency-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-white inline-block" />
          {urgencyText}
        </div>

        <h1
          className="font-display text-4xl sm:text-6xl leading-[1] tracking-tight"
          data-testid="page-title"
        >
          {heroTitleLine1} <span className="italic">{heroTitleItalic}</span> {heroTitleLine2}
        </h1>

        <p className="mt-5 text-base sm:text-lg text-black/85 leading-relaxed font-medium max-w-xl">
          {subtitleText}
        </p>

        {/* PRIMARY + SECONDARY CTAs */}
        <div className="mt-7 flex flex-col sm:flex-row gap-3" data-testid="hero-cta">
          <Link
            to={`/register/${FEATURED.id}`}
            onClick={() => trackEvent("hero_reserve_click")}
            className="inline-flex items-center justify-center gap-2 bg-black text-[#FF6A1A] font-bold px-6 py-4 rounded-full text-base hover:opacity-90 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.45)]"
            data-testid="hero-reserve-btn"
          >
            <span>{content.hero.primary_cta}</span>
            <ArrowUpRight size={16} strokeWidth={2.4} />
          </Link>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("hero_whatsapp_click")}
            className="inline-flex items-center justify-center gap-2 bg-white/60 backdrop-blur border-2 border-black/20 text-black font-semibold px-6 py-4 rounded-full text-base hover:bg-white/80"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle size={16} strokeWidth={2.2} />
            <span>{content.hero.secondary_cta}</span>
          </a>
        </div>

        {/* Quick trust points */}
        <ul className="mt-7 grid grid-cols-2 gap-x-4 gap-y-2.5" data-testid="hero-trust">
          {(content.hero.trust_bullets || []).map((t) => (
            <li key={t} className="flex items-start gap-2 text-sm sm:text-[15px] font-semibold text-black/85">
              <Check size={16} strokeWidth={2.6} className="shrink-0 mt-0.5 text-[#FF6A1A]" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ============ TWIJFEL-OPVANGER ============ */}
      <section className="mt-4 mb-4" data-testid="doubt-section">
        <div className="rounded-3xl bg-white/50 backdrop-blur border border-black/15 px-5 py-6 sm:px-6 shadow-[0_8px_22px_-8px_rgba(0,0,0,0.12)]">
          <div className="flex items-start gap-3">
            <HelpCircle size={22} strokeWidth={1.8} className="text-[#FF6A1A] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight">
                Twijfel je of deze cursus <span className="italic">bij jou past?</span>
              </h2>
              <p className="mt-3 text-sm sm:text-base text-black/80 font-medium leading-relaxed">
                {content.sales_texts?.doubt_text}
              </p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("hero_whatsapp_click")}
                className="mt-5 inline-flex items-center gap-2 bg-[#25D366] text-black font-bold px-5 py-3 rounded-full text-sm hover:opacity-90 shadow-[0_8px_20px_-6px_rgba(37,211,102,0.5)]"
                data-testid="doubt-whatsapp-btn"
              >
                <MessageCircle size={15} strokeWidth={2.2} />
                <span>App Quimarich eerst</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONCRETE RESULTAAT ============ */}
      <section className="mt-14" data-testid="outcome-section">
        <div className="label-tag mb-3">— Concreet resultaat</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Wat je meeneemt <span className="italic">na 3 dagen</span>
        </h2>
        <div className="mt-6 glass-block">
          <p
            className="text-[#FF6A1A]/95 text-base font-medium leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content.sales_texts?.outcome_html || "" }}
          />
        </div>
      </section>

      {/* ============ VOOR WIE IS DIT? ============ */}
      <section className="mt-12" data-testid="for-who-section">
        <div className="label-tag mb-3">— Voor wie is dit?</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Is deze cursus <span className="italic">voor jou?</span>
        </h2>
        <div className="mt-6 rounded-3xl bg-white/50 backdrop-blur border border-black/15 px-5 py-5 shadow-[0_6px_18px_-6px_rgba(0,0,0,0.10)]">
          <div className="flex items-start gap-3">
            <Users size={20} strokeWidth={1.8} className="text-[#FF6A1A] shrink-0 mt-0.5" />
            <p
              className="text-sm sm:text-base font-medium leading-relaxed text-black/85"
              dangerouslySetInnerHTML={{ __html: content.sales_texts?.for_who_html || "" }}
            />
          </div>
        </div>
      </section>

      {/* ============ WAT LEER JE? (SPECIFIC BULLETS) ============ */}
      <section className="mt-14" data-testid="curriculum-section">
        <div className="label-tag mb-3">— Wat je leert</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Concreet, praktisch,<br /><span className="italic">direct toepasbaar</span>
        </h2>

        <ul className="mt-6 space-y-2.5" data-testid="curriculum-bullets">
          {(content.sales_texts?.curriculum_bullets || []).map((b) => (
            <li
              key={b}
              className="flex items-start gap-3 rounded-2xl bg-white/45 backdrop-blur border border-black/15 px-4 py-3 shadow-[0_4px_12px_-6px_rgba(0,0,0,0.10)]"
            >
              <span className="shrink-0 w-6 h-6 rounded-full bg-[#FF6A1A] flex items-center justify-center mt-0.5">
                <Check size={13} strokeWidth={3} className="text-black" />
              </span>
              <span className="text-sm sm:text-base font-medium leading-snug text-black/90 pt-0.5">{b}</span>
            </li>
          ))}
        </ul>

        <SectionCTA waUrl={waUrl} />
      </section>

      {/* ============ WAT KUN JIJ NA DEZE CURSUS ============ */}
      <section className="mt-14" data-testid="results-section">
        <div className="label-tag mb-3">— Groei die je meeneemt</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Wat kun jij na <span className="italic">deze cursus?</span>
        </h2>

        <ul className="mt-6 space-y-3">
          {RESULTS.map((r) => (
            <li
              key={r}
              className="flex items-start gap-3 rounded-2xl bg-white/45 backdrop-blur border border-black/15 px-4 py-3.5"
            >
              <span className="shrink-0 w-7 h-7 rounded-full bg-[#FF6A1A] flex items-center justify-center mt-0.5">
                <Target size={13} strokeWidth={2.8} className="text-black" />
              </span>
              <span className="text-base font-semibold leading-snug text-black/90 pt-0.5">{r}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ============ STUDENT RESULTS (VIDEOS) ============ */}
      <section className="mt-14" data-testid="student-results-section">
        <div className="label-tag mb-3">— Resultaten</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Waarom kiezen cursisten voor <span className="italic">Pattern Texture Academy?</span>
        </h2>
        <p className="mt-4 text-base text-black/75 font-medium max-w-xl">
          Echt werk uit onze cursussen. Bekijk de video&apos;s hieronder.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3" data-testid="student-gallery">
          {[
            { src: "https://customer-assets.emergentagent.com/job_leave-out-pro/artifacts/634njqlf_0E72B391-8D06-4B67-9918-29E09C962822.MOV", label: "Eindwerk" },
            { src: "https://customer-assets.emergentagent.com/job_leave-out-pro/artifacts/r681vdd1_4BB718D7-E71C-4E49-A4D7-A2CC225ABC81.MOV", label: "Praktijk" },
            { src: "https://customer-assets.emergentagent.com/job_leave-out-pro/artifacts/erjfapnk_7F777F2E-0BA6-4BB2-B392-C02CBB836842.MOV", label: "Installatie" },
            { src: "https://customer-assets.emergentagent.com/job_leave-out-pro/artifacts/kxkveztk_8F31C758-F778-4422-9A2D-7ADC3923961E.MOV", label: "Resultaat" },
          ].map((v, i) => (
            <LazyVideo key={i} src={v.src} label={v.label} testid={`student-video-${i}`} />
          ))}
        </div>

        <div className="mt-4 rounded-2xl bg-white/45 backdrop-blur border border-black/15 px-4 py-3 flex items-start gap-2.5 text-xs text-black/75 font-medium">
          <Sparkles size={14} strokeWidth={2} className="shrink-0 mt-0.5 text-[#FF6A1A]" />
          <span>
            Meer op{" "}
            <a href="https://www.instagram.com/Pt.salon.room/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
              Instagram (@Pt.salon.room)
            </a>
          </span>
        </div>

        <SectionCTA waUrl={waUrl} />
      </section>

      {/* ============ OVER PATTERN TEXTURE ============ */}
      <section className="mt-14" data-testid="trainer-section">
        <div className="label-tag mb-3">— De docent</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Over <span className="italic">Pattern Texture</span>
        </h2>

        <div className="mt-6 glass-block">
          <div className="flex flex-col sm:flex-row gap-5">
            <div
              className="shrink-0 w-32 h-32 sm:w-36 sm:h-36 mx-auto sm:mx-0 rounded-2xl overflow-hidden border-2 border-[#FF6A1A]/40 shadow-[0_10px_24px_-8px_rgba(255,106,26,0.35)]"
              data-testid="trainer-photo"
            >
              <img
                src={content.trainer?.photo || TRAINER.photo}
                alt={content.trainer?.name || TRAINER.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="font-display text-3xl text-[#FF6A1A] leading-tight">{content.trainer?.name}</div>
              <div className="label-tag text-[#FF6A1A]/75 mt-1">{content.trainer?.role}</div>
              <p className="mt-4 text-[#FF6A1A]/95 text-sm font-medium leading-relaxed">
                {content.trainer?.intro}
              </p>
              <p className="mt-3 text-[#FF6A1A]/85 text-sm font-medium leading-relaxed">
                {content.trainer?.passion}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURED COURSE — WEAVE MET LEAVE-OUT ============ */}
      <section id="course-dates" className="mt-14 scroll-mt-8">
        <div className="label-tag mb-3">— Deze cursus reserveren</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Weave Met <span className="italic">Leave-Out</span>
        </h2>
        <p className="mt-4 text-base text-black/75 font-medium max-w-md">
          17, 18 &amp; 19 juli — Rotterdam. Beperkt beschikbaar.
        </p>

        <div className="mt-6">
          <CourseCard
            course={FEATURED}
            override={cmsCourseById[FEATURED.id]}
            availability={availability[FEATURED.id]}
            onApply={() => { trackEvent("course_card_click", { metadata: { course_id: FEATURED.id } }); goToReserve(); }}
            featured
          />
        </div>
      </section>

      {/* ============ ANDERE CURSUSSEN ============ */}
      <section className="mt-14" data-testid="other-courses-section">
        <div className="label-tag mb-3">— Ook interessant</div>
        <h2 className="font-display text-3xl sm:text-4xl leading-[1] tracking-tight">
          Andere <span className="italic">cursussen</span>
        </h2>
        <p className="mt-3 text-sm text-black/70 font-medium max-w-md">
          Ook beschikbaar dit jaar.
        </p>

        <div className="mt-6 space-y-4">
          {OTHER_COURSES.filter((c) => cmsCourseById[c.id]?.visible !== false).map((c) => (
            <CourseCard
              key={c.id}
              course={c}
              override={cmsCourseById[c.id]}
              availability={availability[c.id]}
              onApply={() => { trackEvent("course_card_click", { metadata: { course_id: c.id } }); navigate(`/register/${c.id}`); }}
              compact
            />
          ))}
        </div>
      </section>

      {/* ============ WAAROM BOEKEN VIA DEZE WEBSITE ============ */}
      <section className="mt-14" data-testid="trust-booking-section">
        <div className="label-tag mb-3">— Vertrouwen</div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[1] tracking-tight">
          Waarom boeken <span className="italic">via deze website?</span>
        </h2>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TRUST_BOOKING.map((t) => (
            <li
              key={t}
              className="flex items-center gap-3 rounded-2xl bg-white/45 backdrop-blur border border-black/15 px-4 py-3.5"
            >
              <Shield size={18} strokeWidth={2} className="shrink-0 text-[#FF6A1A]" />
              <span className="text-sm sm:text-base font-semibold text-black/90">{t}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ============ FAQ (CMS-controlled) ============ */}
      {faq.length > 0 && (
        <section className="mt-14" data-testid="faq-section">
          <div className="label-tag mb-3">— Veelgestelde vragen</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-[1] tracking-tight">
            Vragen? <span className="italic">Antwoorden.</span>
          </h2>
          <div className="mt-6 space-y-3">
            {faq.map((item, i) => (
              <details
                key={i}
                className="rounded-2xl bg-white/45 backdrop-blur border border-black/15 px-4 py-3"
                data-testid={`faq-item-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 font-semibold text-black/90 text-sm sm:text-base">
                  <span>{item.q}</span>
                  <ChevronDown size={16} className="shrink-0 text-[#FF6A1A]" />
                </summary>
                <p className="mt-3 text-sm text-black/80 font-medium leading-relaxed whitespace-pre-wrap">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* ============ FINAL CTA ============ */}
      <section className="mt-14" data-testid="final-cta-section">
        <div className="rounded-3xl bg-black/90 text-[#FF6A1A] px-6 py-9 sm:px-8 sm:py-12 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.6)]">
          <div className="label-tag opacity-90 mb-4">— Klaar om te starten?</div>
          <h2 className="font-display text-3xl sm:text-5xl leading-[1] tracking-tight">
            {content.sales_texts?.final_cta_title}
          </h2>
          <p className="mt-5 text-base sm:text-lg opacity-90 font-medium leading-relaxed max-w-xl">
            {content.sales_texts?.final_cta_body}
          </p>
          <Link
            to={`/register/${FEATURED.id}`}
            onClick={() => trackEvent("section_cta_reserve_click", { metadata: { section: "final_cta" } })}
            className="mt-7 inline-flex items-center gap-2 bg-[#FF6A1A] text-black font-bold px-6 py-4 rounded-full text-base hover:opacity-90 shadow-[0_10px_24px_-8px_rgba(255,106,26,0.5)]"
            data-testid="final-cta-button"
          >
            <Star size={16} strokeWidth={2.4} />
            <span>{content.hero.primary_cta}</span>
            <ArrowUpRight size={16} strokeWidth={2.4} />
          </Link>
        </div>
      </section>

      {/* ============ WHATSAPP CONTACT ============ */}
      <section className="mt-10 mb-4" data-testid="whatsapp-contact-section">
        <div className="glass-block">
          <div className="flex items-center gap-3 mb-3">
            <MessageCircle size={22} strokeWidth={1.7} className="text-[#FF6A1A]" />
            <div className="label-tag text-[#FF6A1A] opacity-90">— Vragen?</div>
          </div>
          <h3 className="font-display text-3xl text-[#FF6A1A] leading-tight">
            Heb je een <span className="italic">vraag?</span>
          </h3>
          <p className="mt-3 text-[#FF6A1A]/95 text-sm font-medium leading-relaxed">
            Weet je nog niet welke cursus bij je past? Stuur gerust een bericht.
          </p>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("section_cta_whatsapp_click", { metadata: { section: "footer" } })}
            className="mt-5 inline-flex items-center gap-2 bg-[#25D366] text-black font-bold px-6 py-3.5 rounded-full text-base hover:opacity-90 shadow-[0_10px_24px_-8px_rgba(37,211,102,0.6)]"
            data-testid="whatsapp-dm-button"
          >
            <MessageCircle size={18} strokeWidth={2.2} />
            <span>Stuur een WhatsApp</span>
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </a>
        </div>
      </section>

      {/* Sticky mobile CTA — hide in preview */}
      {!previewContent && <StickyCTA reservePath={`/register/${FEATURED.id}`} />}
    </Layout>
  );
}

/* ===================== Reusable CTA row (Reserveer + Vraag stellen) ===================== */

function SectionCTA({ waUrl }) {
  return (
    <div className="mt-6 flex flex-col sm:flex-row gap-2.5" data-testid="section-cta">
      <Link
        to={`/register/${FEATURED.id}`}
        onClick={() => trackEvent("section_cta_reserve_click")}
        className="flex-1 inline-flex items-center justify-center gap-2 bg-black text-[#FF6A1A] font-bold px-5 py-3.5 rounded-full text-sm hover:opacity-90"
        data-testid="section-cta-reserve"
      >
        Reserveer mijn plek
        <ArrowUpRight size={14} strokeWidth={2.4} />
      </Link>
      <a
        href={waUrl || WHATSAPP_DM_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("section_cta_whatsapp_click")}
        className="flex-1 inline-flex items-center justify-center gap-2 bg-white/60 backdrop-blur border-2 border-black/20 text-black font-semibold px-5 py-3.5 rounded-full text-sm hover:bg-white/80"
        data-testid="section-cta-whatsapp"
      >
        <MessageCircle size={14} strokeWidth={2.2} />
        Ik heb eerst een vraag
      </a>
    </div>
  );
}

/* ===================== Course Card ===================== */

function CourseCard({ course, override, availability, onApply, featured = false, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const info = COURSE_INFO[course.id];

  // Resolve values with CMS override precedence: override > availability > course default.
  const total = override?.total_spots ?? course.totalSpots;
  const manualRemaining = override?.available_spots;
  const autoRemaining = availability ? availability.remaining : null;
  const remainingRaw = manualRemaining ?? autoRemaining ?? total;
  const forcedSoldOut = (override?.status === "sold_out") || (override?.available_spots === 0);
  const remaining = forcedSoldOut ? 0 : remainingRaw;
  const soldOut = remaining <= 0;
  const lowAvailability = !soldOut && remaining <= 2;

  const displayName = override?.name || course.name;
  const displaySubtitle = override?.subtitle || course.subtitle;
  const displayPrice = override?.price ?? course.price;
  const displayDateLine = override?.date_line || course.dateLine;
  const ctaLabel = override?.cta_text || "Reserveer Mijn Plek";

  return (
    <article className="glass-block" data-testid={`course-${course.id}-button`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="label-tag opacity-80">{displaySubtitle}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {info && (
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="w-9 h-9 rounded-full bg-[#FF6A1A]/15 border border-[#FF6A1A]/40 text-[#FF6A1A] hover:bg-[#FF6A1A]/25 flex items-center justify-center"
                  data-testid={`info-button-${course.id}`}
                  aria-label={`Meer info over ${displayName}`}
                >
                  <Info size={16} strokeWidth={2} />
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#FF6A1A] border-2 border-black text-black max-w-lg p-0 overflow-hidden rounded-3xl">
                <div className="p-6 sm:p-7">
                  <DialogHeader className="text-left">
                    <div className="label-tag mb-2">{displaySubtitle}</div>
                    <DialogTitle className="font-display text-3xl sm:text-4xl leading-tight text-black tracking-tight">
                      {displayName}
                    </DialogTitle>
                  </DialogHeader>
                  <p className="mt-4 text-base font-medium leading-relaxed text-black/85">{info.summary}</p>
                  <div className="mt-6">
                    <div className="label-tag mb-3">— Wat je leert</div>
                    <ul className="space-y-2">
                      {info.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm font-medium">
                          <Check size={15} strokeWidth={2.4} className="shrink-0 mt-0.5" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-6 pt-5 border-t border-black/30">
                    <div className="label-tag mb-1.5">— Perfect voor</div>
                    <p className="text-sm font-medium leading-relaxed">{info.perfectFor}</p>
                  </div>
                </div>
                <DialogClose className="absolute right-3 top-3 w-8 h-8 rounded-full bg-black text-[#FF6A1A] flex items-center justify-center hover:opacity-90">
                  <X size={14} strokeWidth={2.4} />
                </DialogClose>
              </DialogContent>
            </Dialog>
          )}
          <div
            className={`flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase border ${
              soldOut
                ? "bg-[#FF6A1A]/10 border-[#FF6A1A]/30 text-[#FF6A1A]/70"
                : lowAvailability
                  ? "bg-red-600 border-red-700 text-white animate-pulse"
                  : "bg-[#FF6A1A] border-[#FF6A1A] text-black"
            }`}
            data-testid={`spot-counter-${course.id}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
            {soldOut ? "Vol" : `Nog ${remaining}/${total}`}
          </div>
        </div>
      </div>

      {!soldOut && (
        <div className="mt-2 text-[11px] tracking-wide uppercase font-bold text-red-700">
          {lowAvailability ? "⏰ Bijna vol — wees er snel bij" : "Beperkte plaatsen beschikbaar"}
        </div>
      )}

      <h3 className={`font-display ${featured || !compact ? "text-4xl sm:text-5xl" : "text-3xl"} leading-none mt-3 text-[#FF6A1A]`}>
        {displayName}
      </h3>

      <div className="mt-5 rounded-2xl bg-[#FF6A1A]/10 border border-[#FF6A1A]/25 px-5 py-4">
        <div className="label-tag text-[#FF6A1A]/85 mb-2">— Cursusdata</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="font-display text-3xl sm:text-4xl text-[#FF6A1A] leading-tight tracking-tight">
            {displayDateLine}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="label-tag text-[#FF6A1A]/80 flex items-center gap-2 mb-2">
          <Clock size={12} strokeWidth={2.2} />
          Cursustijden
        </div>
        <ul className="space-y-1">
          {course.schedule.map((s) => (
            <li key={s} className="text-[#FF6A1A]/95 text-sm font-medium">{s}</li>
          ))}
        </ul>
      </div>

      <div className="mt-6 pt-4 border-t border-[#FF6A1A]/25 flex items-center justify-between gap-4">
        <div>
          <div className="label-tag text-[#FF6A1A]/70 mb-1">Investering</div>
          <div className="font-display text-4xl text-[#FF6A1A] leading-none">
            €{Number(displayPrice).toLocaleString("en-US")}
          </div>
        </div>
        <div className="text-right">
          <button
            type="button"
            disabled={soldOut}
            className={`text-sm font-semibold tracking-wide px-5 py-3 rounded-full border transition-colors flex items-center gap-2 ${
              soldOut
                ? "bg-transparent border-[#FF6A1A]/30 text-[#FF6A1A]/40 cursor-not-allowed"
                : "bg-[#FF6A1A] border-[#FF6A1A] text-black hover:opacity-90"
            }`}
            onClick={(e) => { e.stopPropagation(); if (!soldOut) onApply(); }}
            data-testid={`course-${course.id}-cta`}
          >
            <span>{soldOut ? "Volgeboekt" : ctaLabel}</span>
            {!soldOut && <ArrowUpRight size={14} strokeWidth={2.2} />}
          </button>
        </div>
      </div>

      {/* WAT IS INBEGREPEN */}
      <div className="mt-4 pt-4 border-t border-[#FF6A1A]/15">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="flex items-center justify-between w-full text-left"
          data-testid={`included-toggle-${course.id}`}
          aria-expanded={expanded}
        >
          <span className="label-tag text-[#FF6A1A]/95">— Wat is inbegrepen?</span>
          {expanded
            ? <ChevronUp size={16} className="text-[#FF6A1A]" />
            : <ChevronDown size={16} className="text-[#FF6A1A]" />}
        </button>

        {expanded && (
          <div className="mt-4" data-testid={`included-content-${course.id}`}>
            <div className="mb-4 rounded-2xl overflow-hidden border border-[#FF6A1A]/25 shadow-[0_8px_22px_-8px_rgba(0,0,0,0.35)]">
              <img
                src={GOODIE_BAG_PHOTO}
                alt="Pattern Texture goodie bag met inhoud"
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#FF6A1A]/70 mb-3 font-mono text-center">
              ↳ Jouw goodie bag op cursusdag
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
              {INCLUDED_DETAILED.map((item) => (
                <li key={item} className="flex items-start gap-2 text-[#FF6A1A]/95 text-sm font-medium">
                  <Check size={14} strokeWidth={2.6} className="shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[#FF6A1A] text-sm font-bold italic">
              Je hoeft zelf niets mee te nemen.
            </p>
          </div>
        )}
      </div>
    </article>
  );
}

/* ===================== Lazy-loaded auto-playing video ===================== */

function LazyVideo({ src, label, testid }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [tapped, setTapped] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, []);

  const handleTap = () => {
    setTapped(true);
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
  };

  return (
    <div
      ref={containerRef}
      onClick={handleTap}
      className="aspect-[9/16] rounded-2xl overflow-hidden relative bg-black border border-black/30 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.4)] cursor-pointer"
      data-testid={testid}
    >
      {inView ? (
        <video
          ref={videoRef}
          src={src}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#FF6A1A]/20 to-black">
          <div className="text-[#FF6A1A]/60 text-[10px] tracking-[0.25em] uppercase font-mono">Laden…</div>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-3 pointer-events-none">
        <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-[#FF6A1A]">{label}</div>
      </div>
      {inView && !tapped && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px] pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-[#FF6A1A] text-black flex items-center justify-center shadow-[0_6px_18px_-6px_rgba(255,106,26,0.6)]">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          </div>
        </div>
      )}
    </div>
  );
}
